import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ClipboardCheck } from 'lucide-react';
import { VehicleCheckedItems, ChecklistItemConfig } from '@/app/types/jobCard';

interface VehicleChecklistProps {
  value: VehicleCheckedItems | null;
  onChange: (value: VehicleCheckedItems) => void;
  checklistConfig: ChecklistItemConfig[];
  disabled?: boolean;
}

// Default checklist configuration if not provided from API
const defaultChecklistConfig: ChecklistItemConfig[] = [
  { key: 'exterior_scratches', label: 'Exterior Scratches', type: 'text' },
  { key: 'exterior_dents', label: 'Exterior Dents', type: 'text' },
  { key: 'spare_tire', label: 'Spare Tire Present', type: 'boolean' },
  { key: 'jack_tools', label: 'Jack & Tools Present', type: 'boolean' },
  { key: 'documents', label: 'Documents in Vehicle', type: 'boolean' },
  { key: 'valuables_note', label: 'Valuables/Accessories Note', type: 'text' },
];

export const VehicleChecklist: React.FC<VehicleChecklistProps> = ({
  value,
  onChange,
  checklistConfig,
  disabled = false,
}) => {
  const config = checklistConfig.length > 0 ? checklistConfig : defaultChecklistConfig;
  const currentValue = value || {};

  const handleChange = (key: string, newValue: boolean | string | null): void => {
    onChange({
      ...currentValue,
      [key]: newValue,
    });
  };

  // Separate boolean and text fields
  const booleanFields = config.filter((item) => item.type === 'boolean');
  const textFields = config.filter((item) => item.type === 'text');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5" />
          Vehicle Condition Checklist
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Boolean Checkboxes */}
        {booleanFields.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {booleanFields.map((item) => (
              <div key={item.key} className="flex items-center space-x-2">
                <Checkbox
                  id={item.key}
                  checked={!!currentValue[item.key as keyof VehicleCheckedItems]}
                  onCheckedChange={(checked) => handleChange(item.key, !!checked)}
                  disabled={disabled}
                />
                <Label
                  htmlFor={item.key}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {item.label}
                </Label>
              </div>
            ))}
          </div>
        )}

        {/* Text Fields */}
        {textFields.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {textFields.map((item) => (
              <div key={item.key} className="space-y-2">
                <Label htmlFor={item.key}>{item.label}</Label>
                <Textarea
                  id={item.key}
                  placeholder={`Enter ${item.label.toLowerCase()}...`}
                  value={(currentValue[item.key as keyof VehicleCheckedItems] as string) || ''}
                  onChange={(e) => handleChange(item.key, e.target.value || null)}
                  disabled={disabled}
                  rows={2}
                />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
